/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatApiErrorDetail } from "@/lib/api";
import Header from "@/components/app/Header";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowsClockwise, ChartLineUp, Users, Warning, ClockClockwise, Lightning, ShieldCheck,
} from "@phosphor-icons/react";

/**
 * `/admin/sla` — partner-level visibility into where the firm is
 * drowning vs caught up. Admin solicitors only.
 *
 * Pulls from `GET /api/sla/firm-wide` which aggregates:
 *   - Active case count per solicitor
 *   - Unread + unreplied counts split by age band
 *   - Average reply time (last 90 days, completed pairs only)
 *
 * The page is read-only. Rows are coloured by computed `health` flag
 * (drowning / busy / ok / caught_up) and clicking a solicitor's name
 * takes you to the Admin Users page filtered to that person.
 */
export default function SlaDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ solicitors: [], totals: {}, as_of: null });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await API.get("/sla/firm-wide");
      setData(resp || { solicitors: [], totals: {}, as_of: null });
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (err.response?.status === 403) {
        toast.error("Admin solicitor access required");
        navigate("/");
      } else {
        toast.error(detail || "Failed to load SLA dashboard");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (user?.role !== "solicitor" || !user?.is_admin) {
      navigate("/");
      return;
    }
    fetchData();
  }, [user, navigate, fetchData]);

  // Auto-refresh every 5 minutes so a partner watching the dashboard sees the
  // numbers tick down as solicitors catch up. Was 60s — that turned into a
  // 4-aggregations-per-minute pile-up on production when several admins
  // had the page open at once, contributing to the 520 outage.
  useEffect(() => {
    const id = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchData]);

  const totals = data.totals || {};
  const fmtHours = (h) => (h == null ? "—" : h < 1 ? "<1h" : h < 24 ? `${h.toFixed(1)}h` : `${(h / 24).toFixed(1)}d`);
  const healthStyles = {
    drowning: { row: "bg-red-50/40", pill: "bg-red-100 text-red-800", label: "Drowning" },
    busy:     { row: "bg-amber-50/40", pill: "bg-amber-100 text-amber-800", label: "Busy" },
    ok:       { row: "bg-white", pill: "bg-slate-100 text-slate-700", label: "OK" },
    caught_up:{ row: "bg-emerald-50/40", pill: "bg-emerald-100 text-emerald-800", label: "Caught up" },
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sla-dashboard-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Firm SLA dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Per-solicitor view of unread messages, unreplied emails, and average reply time across the last 90 days. Admins only.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium" title="Auto-refreshing every 60 seconds">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              Live
            </span>
            <Button onClick={fetchData} disabled={loading} variant="outline" className="gap-1.5" data-testid="sla-refresh-btn">
              <ArrowsClockwise weight="bold" className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Firm totals row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="sla-totals-row">
          <StatCard testId="sla-total-cases" label="Active cases" value={totals.active_cases ?? 0} icon={<Users weight="duotone" className="w-5 h-5 text-slate-400" />} />
          <StatCard testId="sla-total-unread" label="Unread" value={totals.unread ?? 0} icon={<ChartLineUp weight="duotone" className="w-5 h-5 text-slate-400" />} />
          <StatCard testId="sla-total-unreplied" label="Unreplied" value={totals.unreplied ?? 0} icon={<ClockClockwise weight="duotone" className="w-5 h-5 text-slate-400" />} />
          <StatCard testId="sla-total-warning" label="Warning (24-48h)" value={totals.warning ?? 0} tone="amber" icon={<Warning weight="duotone" className="w-5 h-5 text-amber-500" />} />
          <StatCard testId="sla-total-critical" label="Critical (>48h)" value={totals.critical ?? 0} tone="red" icon={<Lightning weight="duotone" className="w-5 h-5 text-red-500" />} />
          <StatCard
            testId="sla-total-reply"
            label="Avg reply (in-product)"
            value={fmtHours(totals.avg_reply_hours)}
            subtitle={`${totals.reply_sample || 0} replies`}
            icon={<ShieldCheck weight="duotone" className="w-5 h-5 text-emerald-500" />}
          />
          <StatCard
            testId="sla-total-reply-all"
            label="Avg reply (incl. manual)"
            value={fmtHours(totals.avg_reply_hours_all)}
            subtitle={`${totals.manual_marks || 0} manual mark${totals.manual_marks === 1 ? "" : "s"}`}
            icon={<ShieldCheck weight="duotone" className="w-5 h-5 text-slate-400" />}
          />
        </div>

        {/* Per-solicitor table */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-900">
              {data.solicitors?.length || 0} solicitor{(data.solicitors?.length || 0) === 1 ? "" : "s"}
            </p>
            <p className="text-[11px] text-slate-400">
              {data.as_of ? `Snapshot: ${new Date(data.as_of).toLocaleString("en-GB")}` : ""}
            </p>
          </div>
          {loading ? (
            <div className="py-12 text-center text-sm text-slate-400">Loading…</div>
          ) : (data.solicitors?.length || 0) === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400" data-testid="sla-empty">
              No active solicitors found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-50">
                    <th className="px-4 py-2.5">Solicitor</th>
                    <th className="px-3 py-2.5 text-right">Active cases</th>
                    <th className="px-3 py-2.5 text-right">Unread</th>
                    <th className="px-3 py-2.5 text-right">Unreplied</th>
                    <th className="px-3 py-2.5 text-right text-amber-700">Warning</th>
                    <th className="px-3 py-2.5 text-right text-red-700">Critical</th>
                    <th className="px-3 py-2.5 text-right" title="Average of inbound→outbound reply pairs (in-product replies only)">Avg reply<br /><span className="font-normal text-slate-400">in-product</span></th>
                    <th className="px-3 py-2.5 text-right" title="Includes pairs resolved by 'Mark replied' manual marks — shows outside-channel catch-up">Avg reply<br /><span className="font-normal text-slate-400">incl. manual</span></th>
                    <th className="px-3 py-2.5 text-right" title="Number of inbound emails marked replied via the Messages tab (outside-channel reply)">Manual<br /><span className="font-normal text-slate-400">marks</span></th>
                    <th className="px-3 py-2.5 text-right" title="Number of inbound emails marked as 'no reply needed' (FYIs, acknowledgements, automated notifs)">No-reply<br /><span className="font-normal text-slate-400">marks</span></th>
                    <th className="px-3 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.solicitors.map((s) => {
                    const styles = healthStyles[s.health] || healthStyles.ok;
                    return (
                      <tr
                        key={s.id}
                        className={`${styles.row} hover:bg-slate-100/40`}
                        data-testid={`sla-row-${s.id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="font-medium text-slate-900">{s.name}</span>
                            <span className="text-[11px] text-slate-500">{s.email}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums text-slate-900">{s.active_cases}</td>
                        <td className="px-3 py-3 text-right tabular-nums">{s.unread || <span className="text-slate-300">0</span>}</td>
                        <td className="px-3 py-3 text-right tabular-nums">{s.unreplied || <span className="text-slate-300">0</span>}</td>
                        <td className="px-3 py-3 text-right tabular-nums">
                          {s.warning > 0 ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-amber-800 bg-amber-100 font-semibold">{s.warning}</span>
                          ) : (
                            <span className="text-slate-300">0</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums">
                          {s.critical > 0 ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-red-800 bg-red-100 font-semibold">{s.critical}</span>
                          ) : (
                            <span className="text-slate-300">0</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums text-slate-700">
                          {fmtHours(s.avg_reply_hours)}
                          {s.reply_sample > 0 && (
                            <span className="text-[10px] text-slate-400 ml-1">({s.reply_sample})</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums text-slate-700">
                          {fmtHours(s.avg_reply_hours_all)}
                          {s.reply_sample_all > 0 && (
                            <span className="text-[10px] text-slate-400 ml-1">({s.reply_sample_all})</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums">
                          {s.manual_marks > 0 ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-amber-800 bg-amber-50 font-semibold" title="Times this solicitor used 'Mark replied' to clear an inbound email">{s.manual_marks}</span>
                          ) : (
                            <span className="text-slate-300">0</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums">
                          {s.no_reply_marks > 0 ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-slate-700 bg-slate-100 font-semibold" title="Times this solicitor cleared an inbound email as 'no reply needed'">{s.no_reply_marks}</span>
                          ) : (
                            <span className="text-slate-300">0</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${styles.pill}`}>
                            {styles.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-[11px] text-slate-400 mt-3">
          <strong>How it's calculated:</strong> "Unread" counts client/referrer messages no solicitor has marked as seen. "Unreplied" counts cases whose latest email is incoming. "Avg reply (in-product)" averages every completed inbound→outbound pair in ConveyTrack over the last 90 days. "Avg reply (incl. manual)" adds pairs resolved via the **Mark replied** button on the Messages tab — useful for spotting solicitors who're catching up via outside channels rather than replying in-product. Single-thread reply gaps capped at 14 days to filter out abandoned threads.
        </p>
      </main>
    </div>
  );
}

function StatCard({ label, value, subtitle, tone, icon, testId }) {
  const toneCls =
    tone === "red" ? "border-red-200" :
    tone === "amber" ? "border-amber-200" :
    "border-slate-200";
  return (
    <div className={`bg-white border ${toneCls} rounded-lg p-3`} data-testid={testId}>
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      </div>
      <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</p>
      {subtitle && <p className="text-[10px] text-slate-400 mt-0.5">{subtitle}</p>}
    </div>
  );
}
